import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Pencil } from 'lucide-react';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { ROUTES } from '#/config/routes';
import { approveStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/approve';
import { cancelStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/cancel';
import { rejectStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/reject';
import { submitStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/submit';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  canApproveStockReceipt,
  canCancelStockReceipt,
  canEditStockReceipt,
  canEditStockReceiptRemark,
  canRejectStockReceipt,
  canSubmitStockReceipt,
} from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import {
  getStockReceiptApiErrorMessage,
  isStockReceiptVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import { toast } from '#/utils/toast';
import {
  stockReceiptDetailQueryKey,
  stockReceiptListQueryKeyPrefix,
  workOrderDetailQueryKey,
} from '#/utils/queryKeys';

const StockReceiptCommandBar = ({ detail, onRefetch }) => {
  const canCreate = useCan(PERM.STOCK_RECEIPT_CREATE);
  const canUpdate = useCan(PERM.STOCK_RECEIPT_UPDATE);
  const canReview = useCan(PERM.STOCK_RECEIPT_REVIEW);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;
  const workOrderId = detail?.sourceType === 'WORK_ORDER' && detail?.sourceId
    ? detail.sourceId
    : null;

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: stockReceiptListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: stockReceiptDetailQueryKey(id) });
    }
    if (workOrderId) {
      await queryClient.invalidateQueries({
        queryKey: workOrderDetailQueryKey(workOrderId),
      });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitStockReceipt(id, payload);
      }
      if (action === 'approve') {
        return approveStockReceipt(id, payload);
      }
      if (action === 'reject') {
        return rejectStockReceipt(id, payload);
      }
      if (action === 'cancel') {
        return cancelStockReceipt(id);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'cancel') {
        await queryClient.invalidateQueries({ queryKey: stockReceiptListQueryKeyPrefix() });
        if (workOrderId) {
          await queryClient.invalidateQueries({
            queryKey: workOrderDetailQueryKey(workOrderId),
          });
        }
        navigate(ROUTES.stockReceipt);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getStockReceiptApiErrorMessage(error));
      if (isStockReceiptVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交入庫單',
      message: '提交後將進入待核准狀態，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准入庫單',
      message: '核准後將建立庫存批次，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel' });
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回入庫單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      {canEditStockReceipt(status) && canUpdate ? (
        <Button asChild variant="outline">
          <Link to={ROUTES.stockReceiptEdit(id)}>編輯草稿</Link>
        </Button>
      ) : null}
      {canEditStockReceiptRemark(status) && canUpdate ? (
        <Button asChild variant="secondary">
          <Link to={ROUTES.stockReceiptRemark(id)}>
            <Pencil className="size-3.5" aria-hidden />
            編輯備註
          </Link>
        </Button>
      ) : null}
      {canSubmitStockReceipt(status) && canCreate ? (
        <Button disabled={isPending} onClick={handleSubmit}>
          提交
        </Button>
      ) : null}
      {canApproveStockReceipt(status) && canReview ? (
        <Button disabled={isPending} onClick={handleApprove}>
          核准
        </Button>
      ) : null}
      {canRejectStockReceipt(status) && canReview ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={() => setRejectDialogOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canCancelStockReceipt(status) && canCreate ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleCancel}
        >
          刪除草稿
        </Button>
      ) : null}
    </>
  );
};

StockReceiptCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    remark: PropTypes.string,
    updatedAt: PropTypes.string,
    sourceType: PropTypes.string,
    sourceId: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default StockReceiptCommandBar;
